import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as yup from "yup";

// Context
import { useBaseState, useBaseDispatch } from "../context/Base.context";
import { useConditionDispatch } from "../context/Condition.context";

// Components
import StatementWrapper from "../components/StatementWrapper/StatementWrapper";
import QuestionErrorMessage from "../components/QuestionErrorMessage/QuestionErrorMessage";
import Button from "../components/Button/Button";
import QuestionStickyHeader from "../components/QuestionStickyHeader/QuestionStickyHeader";
import Container from "../components/Container/Container";

// Interfaces
import Question from "../types/Question.interface";
import Statement from "../types/Statement.interface";
import InitialValues from "../types/InitialValues.interface";

// Misc.
import { formatDateToHumanReadable } from "../lib/Date.lib";
import { createInitialValues } from "../lib/Form.lib";
import createYupSchema from "../lib/SchemaCreator.lib";
import useProgress from "../lib/useProgress.lib";
import { isArray } from "../lib/Array.lib";

const QuestionPage: React.FC = () => {
  const history = useHistory();
  const { questionId, categoryId } = useParams<any>();
  const baseState = useBaseState();
  const baseDispatch = useBaseDispatch();
  const conditionDispatch = useConditionDispatch();
  const [question, setQuestion] = React.useState<Question | null>(null);
  const [initialValues, setInitialValues] = React.useState<InitialValues>({});
  const [validationSchema, setValidationSchema] = React.useState<any>(null);
  const [progress] = useProgress(question);

  React.useEffect(() => {
    Object.keys(initialValues).forEach((key) => {
      const initialValue = initialValues[key] as any;

      if (isArray(initialValue) && initialValue.length > 0) {
        // InitialValue is an Array and Array is not empty
        conditionDispatch({
          type: "add",
          payload: {
            [key]: [...initialValue],
          },
        });
      } else if (initialValue.value && initialValue.value !== "") {
        // InitialValue is an Object and Object.value is not empty
        const { id, value } = initialValue;
        conditionDispatch({
          type: "add",
          payload: {
            [key]: { id, value },
          },
        });
      }
    });
  }, [initialValues, conditionDispatch]);

  React.useEffect(() => {
    if (questionId && categoryId && baseState && !question) {
      fetch(`/resilience-scanner/api/v1/question/${questionId}`)
        .then((response) => response.json())
        .then((questionData) => {
          setInitialValues(createInitialValues(questionData, baseState));
          setQuestion(questionData);
        });
    }
  }, [questionId, categoryId, baseState, question]);

  React.useEffect(() => {
    if (question) {
      const yupSchema = question.statements.reduce(createYupSchema, {});
      setValidationSchema(yup.object().shape(yupSchema));
    }
  }, [question]);

  return (
    <div className="bg-dw-grey-03 flex-grow">
      <Container>
        {question && (
          <QuestionStickyHeader progress={progress} categoryId={categoryId} />
        )}
        <div className="w-11/12 xxl:w-full mx-auto">
          {question && initialValues && validationSchema && (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={(values) => {
                const payload = {
                  [question._id]: values,
                };

                baseDispatch({ type: "add", payload });
                history.push(`/category/${categoryId}`);
              }}
            >
              {({ isValid, errors, submitCount }) => (
                <Form>
                  <div className="mb-3">
                    <p className="text-xl mb-2 font-serif text-dw-blue">
                      {question.question}
                    </p>
                    <p className="text-xs text-dw-grey-11">
                      Last updated
                      <span className="text-xs text-dw-grey-11 ml-1">
                        {formatDateToHumanReadable(question.updatedAt)}
                      </span>
                    </p>
                  </div>
                  <p className="font-bold text-dw-grey-13 mb-10">
                    {question.intro}
                  </p>
                  <div className="grid gap-x-4 gap-y-5 lg:grid-cols-2 mb-5">
                    {question.statements.map((statement: Statement) => (
                      <StatementWrapper
                        key={statement._id}
                        statement={statement}
                      />
                    ))}
                  </div>
                  {!isValid && submitCount > 0 && (
                    <QuestionErrorMessage
                      isValid={isValid}
                      errors={errors}
                      submitCount={submitCount}
                    />
                  )}
                  <Button type="submit">Save and return to topic</Button>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </Container>
    </div>
  );
};

export default QuestionPage;
